/**
 * Medicare lookup API — typed calls over the shared axios instance
 * (contracts-plan.md Appendix A "Medicare", §13.5 WP-43; §7.6, §5.7; R18,
 * decision D13).
 *
 * Per the ixverify precedent ("No new HTTP client, no ungated call") this file
 * never imports axios directly — only `services/api`, exactly the way
 * `frontend/src/contracts/api/client.ts` and `api/rateCard.ts` do. It is a
 * SEPARATE module (not an addition to client.ts) because another WP appends to
 * client.ts concurrently (orchestrator hand-off, 2026-09-11); the MOUNT step
 * re-exports these names from client.ts so callers eventually see one surface
 * (`backend/src/routes/contracts/medicare.mount.md` §4).
 *
 * Types mirror the shapes `backend/src/services/contracts/medicare/README.md`
 * documents.
 */
import api from '../../services/api';
import type { RateCardEntryRow, RcScope } from './rateCard';

/** G8's three labels; a Medicare amount is always derived (computed from RVUs, GPCIs and the conversion factor). */
export type MedicareLabel = 'derived';

/** One locality's two amounts for a code, with the provenance §5.7 stores (G7). */
export interface MedicareLocalityRate {
  id: string;
  hcpcs: string;
  modifier: string | null;
  year: number;
  locality_code: string;
  locality_name: string;
  /** CMS publishes no amount for a setting whose NA indicator is set — null, never 0. */
  non_facility_rate: number | null;
  facility_rate: number | null;
  label: MedicareLabel;
  source_url: string;
  fetched_at: string;
}

/** A §5.2 master entry whose CPT or HCPCS code is the looked-up code, with its card rows. */
export interface MedicareMatchedEntry {
  entry_id: string;
  official_name: string;
  list_type: string;
  section: string | null;
  code_type: string | null;
  code: string | null;
  /** WP-40's card rows for this entry — Low, Preferred, High, count, median, latest, datapoint_ids. */
  values: RateCardEntryRow[];
}

export interface MedicareLookupResult {
  code: string;
  year: number;
  /** The modifier filtered on, or null when every variant was returned. */
  modifier: string | null;
  modifier_filtered: boolean;
  localities: MedicareLocalityRate[];
  entries: MedicareMatchedEntry[];
  card: { scope: RcScope; site_id: string | null; therapeutic_area_id: string | null };
}

export interface MedicareLookupParams {
  code: string;
  year?: number | string | null;
  /**
   * Three-valued, matching the service (medicare/lookup.js): omit the key for
   * every variant of the code, pass `null` for the base row with no modifier,
   * pass a string for that modifier.
   */
  modifier?: string | null;
  scope?: RcScope;
  siteId?: string | null;
  therapeuticAreaId?: string | null;
}

/**
 * GET /medicare/lookup?code&year&modifier — one code's amounts for the
 * configured localities, beside the card's Low, Preferred and High (§7.6).
 * A code with no imported rate for the year is a 404.
 */
export async function lookupMedicare(params: MedicareLookupParams): Promise<MedicareLookupResult> {
  const query: Record<string, string> = { code: params.code };
  if (params.year !== undefined && params.year !== null && params.year !== '') query.year = String(params.year);
  // An empty string is meaningful here: it asks for the base row.
  if (params.modifier !== undefined) query.modifier = params.modifier ?? '';
  if (params.scope) query.scope = params.scope;
  if (params.scope !== 'network' && params.siteId) query.siteId = params.siteId;
  if (params.therapeuticAreaId) query.therapeuticAreaId = params.therapeuticAreaId;

  const response = await api.get<MedicareLookupResult>('/api/contracts/medicare/lookup', { params: query });
  return response.data;
}

/** One locality of a release, as the import report lists it. */
export interface MedicareImportLocality {
  locality_code: string;
  locality_name: string;
  state: string | null;
  mac: string | null;
  pw_gpci: number;
  pe_gpci: number;
  mp_gpci: number;
}

export interface MedicareImportResult {
  year: number;
  source_url: string;
  fetched_at: string;
  conversion_factor: number;
  files: { rvu: string; gpci: string };
  locality_selectors: string[];
  localities: MedicareImportLocality[];
  /** G9's inventory: what was read, what was priced, what was not. */
  codes: { read: number; priced: number; skipped: number };
  rates: { built: number; inserted: number; updated: number; unchanged: number };
  skipped_by_reason: Record<string, number>;
  skipped_sample: Array<{ hcpcs: string; modifier: string | null; reason: string; detail: string }>;
}

export interface MedicareImportInput {
  year: number;
  /** A CMS release ZIP, or the two CSVs addressed directly. Never guessed (G3). */
  sourceUrls: { zip: string } | { rvu: string; gpci: string };
  /** Locality selectors; omit to use CONTRACTS_MEDICARE_LOCALITIES (Appendix B). */
  localities?: string | string[] | null;
  /** The year's conversion factor, when the release's CONV FACTOR column is absent. */
  conversionFactor?: number | null;
}

/** POST /medicare/import — the owner-triggered yearly import (§7.6, R18, G7). */
export async function importMedicare(input: MedicareImportInput): Promise<MedicareImportResult> {
  const response = await api.post<MedicareImportResult>('/api/contracts/medicare/import', input);
  return response.data;
}
